"use client";

import React, { useState } from "react";
import { 
  Building, 
  Calendar, 
  Car, 
  Phone, 
  MessageSquare, 
  ChevronRight, 
  ArrowRight, 
  Sparkles, 
  CheckCircle2, 
  Clock, 
  Layers, 
  Search,
  Filter,
  Check
} from "lucide-react";
import { RealEstateLead } from "@/lib/demos/realEstateDb";

interface RealEstateMobilePipelineProps {
  leads: RealEstateLead[];
  onSelectLead: (lead: RealEstateLead) => void;
  onUpdateStage: (leadId: number, newStage: RealEstateLead["stage"]) => void;
  currency?: "INR" | "USD";
}

const STAGES: { id: RealEstateLead["stage"]; title: string; shortTitle: string; dotColor: string }[] = [
  { id: "New Inquiries", title: "New Inquiries", shortTitle: "Inquiries", dotColor: "bg-blue-400" },
  { id: "Site Visit Scheduled", title: "Site Visit Scheduled", shortTitle: "Visit Booked", dotColor: "bg-amber-400" },
  { id: "Site Visit Completed", title: "Visit Completed", shortTitle: "Completed", dotColor: "bg-purple-400" },
  { id: "Booking Token", title: "Booking Token (EOI)", shortTitle: "Token Paid", dotColor: "bg-orange-400" },
  { id: "Sale Deed (Won)", title: "Sale Deed (Won)", shortTitle: "Deed Won", dotColor: "bg-emerald-400" },
];

export default function RealEstateMobilePipeline({
  leads,
  onSelectLead,
  onUpdateStage,
  currency = "INR",
}: RealEstateMobilePipelineProps) {
  const [activeStage, setActiveStage] = useState<RealEstateLead["stage"] | "ALL">("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [movingLeadId, setMovingLeadId] = useState<number | null>(null);

  // Filter leads by search query and active stage
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery) ||
      lead.property_name.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStage = activeStage === "ALL" || lead.stage === activeStage;
    return matchesSearch && matchesStage;
  });

  // Calculate gross value for active stage
  const totalValue = filteredLeads.reduce((acc, lead) => acc + (lead.budget_numeric || 0), 0);
  const formattedTotal = () => {
    if (currency === "USD") {
      const valUsd = totalValue / 85;
      return `$${(valUsd / 1_000_000).toFixed(2)}M`;
    }
    return `₹${(totalValue / 10_000_000).toFixed(2)} Cr`;
  };

  const handleCall = (e: React.MouseEvent, phone?: string | null) => {
    e.stopPropagation();
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const handleWhatsApp = (e: React.MouseEvent, lead: RealEstateLead) => {
    e.stopPropagation();
    if (!lead.phone) return;
    const clean = lead.phone.replace(/\D/g, "");
    const formattedPhone = clean.length === 10 ? `91${clean}` : clean;
    const text = encodeURIComponent(
      `Hello ${lead.name}! This is Prestige Real Estate regarding your inquiry for ${lead.property_name}.`
    );
    window.open(`https://wa.me/${formattedPhone}?text=${text}`, "_blank");
  };

  return (
    <div className="space-y-4 pb-12">
      {/* Mobile Sticky Search & Stage Selector Header */}
      <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur-md pt-2 pb-3 border-b border-slate-800/80 space-y-3">
        {/* Quick Search */}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search leads by name, phone, property..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 shadow-inner"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>

        {/* Horizontal Scrollable Stage Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
          {/* All Leads Tab */}
          <button
            onClick={() => setActiveStage("ALL")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition-all flex items-center gap-1.5 border ${
              activeStage === "ALL"
                ? "bg-amber-500 text-slate-950 border-amber-400 shadow-md shadow-amber-500/20"
                : "bg-slate-900 text-slate-400 border-slate-800 hover:border-slate-700"
            }`}
          >
            <span>All Leads</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeStage === "ALL" ? "bg-slate-950/30 text-slate-950" : "bg-slate-800 text-slate-300"}`}>
              {leads.length}
            </span>
          </button>

          {/* Individual Stage Tabs */}
          {STAGES.map((s) => {
            const count = leads.filter((l) => l.stage === s.id).length;
            const isSelected = activeStage === s.id;

            return (
              <button
                key={s.id}
                onClick={() => setActiveStage(s.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition-all flex items-center gap-1.5 border ${
                  isSelected
                    ? "bg-slate-800 text-white border-amber-500 shadow-md shadow-amber-500/10"
                    : "bg-slate-900 text-slate-400 border-slate-800 hover:border-slate-700"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${s.dotColor}`} />
                <span>{s.shortTitle}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? "bg-amber-500/20 text-amber-300" : "bg-slate-800 text-slate-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Stage Summary Bar */}
        <div className="flex items-center justify-between px-1 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-amber-400" />
            <span>Showing <strong>{filteredLeads.length}</strong> leads</span>
          </div>
          <div className="text-[11px]">
            Gross Stage Pipeline: <strong className="text-amber-400">{formattedTotal()}</strong>
          </div>
        </div>
      </div>

      {/* Leads Stream */}
      {filteredLeads.length === 0 ? (
        <div className="text-center py-16 px-4 rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/40">
          <p className="text-sm font-semibold text-slate-300">No leads found in this view</p>
          <p className="text-xs text-slate-500 mt-1">Try selecting another stage or clearing your search.</p>
          <button
            onClick={() => { setActiveStage("ALL"); setSearchQuery(""); }}
            className="mt-4 px-4 py-2 rounded-xl bg-slate-800 text-white text-xs font-bold hover:bg-slate-700"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <div
              key={lead.id}
              onClick={() => onSelectLead(lead)}
              className="rounded-2xl p-4 bg-slate-900/90 border border-slate-800 shadow-lg hover:border-amber-500/40 active:scale-[0.99] transition-all"
            >
              {/* Top Row: Type, ID & Budget */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-950 border border-slate-800 text-amber-400 uppercase tracking-wide">
                    {lead.property_type}
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400">
                    #{lead.id}
                  </span>
                </div>

                <div className="text-xs font-black text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                  {currency === "USD" ? lead.budget_range : lead.budget_range}
                </div>
              </div>

              {/* Lead Name & Property */}
              <div>
                <h4 className="text-base font-extrabold text-white">
                  {lead.name}
                </h4>
                <div className="flex items-center gap-1.5 text-xs text-slate-300 font-medium mt-0.5">
                  <Building className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span className="truncate">{lead.property_name}</span>
                </div>
              </div>

              {/* Current Stage Badge */}
              <div className="mt-3 flex items-center justify-between text-xs py-1.5 px-2.5 rounded-xl bg-slate-950/80 border border-slate-800/80">
                <span className="text-slate-400 text-[11px]">Stage:</span>
                <div className="flex items-center gap-1.5 font-bold text-white text-xs">
                  <span className={`w-2 h-2 rounded-full ${STAGES.find(s => s.id === lead.stage)?.dotColor || "bg-amber-400"}`} />
                  <span>{lead.stage}</span>
                </div>
              </div>

              {/* Site Visit Indicator (if any) */}
              {lead.site_visit_date && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
                  <Clock className="w-3 h-3 text-amber-400 shrink-0" />
                  <span>{lead.site_visit_date}</span>
                  {lead.site_visit_pickup ? (
                    <span className="flex items-center gap-0.5 text-cyan-400 font-semibold text-[10px]">
                      <Car className="w-3 h-3" /> Chauffeur
                    </span>
                  ) : null}
                </div>
              )}

              {/* 1-Tap Mobile Quick Action Bar */}
              <div className="mt-3.5 pt-3 border-t border-slate-800 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {/* WhatsApp Quick Action */}
                  <button
                    onClick={(e) => handleWhatsApp(e, lead)}
                    className="p-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5 text-xs font-bold active:scale-95 transition-all"
                    title="Send WhatsApp"
                  >
                    <MessageSquare className="w-4 h-4 fill-emerald-400/20" />
                    <span>WhatsApp</span>
                  </button>

                  {/* Phone Call Quick Action */}
                  <button
                    onClick={(e) => handleCall(e, lead.phone)}
                    className="p-2.5 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1.5 text-xs font-bold active:scale-95 transition-all"
                    title="Direct Call"
                  >
                    <Phone className="w-4 h-4 fill-blue-400/20" />
                    <span>Call</span>
                  </button>
                </div>

                {/* 1-Tap Move Stage Action */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMovingLeadId(movingLeadId === lead.id ? null : lead.id);
                  }}
                  className="px-3 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-bold flex items-center gap-1 active:scale-95 transition-all"
                >
                  <span>Move Stage</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Quick Move Stage Drawer (when clicked) */}
              {movingLeadId === lead.id && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="mt-3 p-3 rounded-xl bg-slate-950 border border-amber-500/40 space-y-2 animate-in fade-in duration-200"
                >
                  <p className="text-[11px] font-bold text-slate-300">Advance {lead.name} to:</p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {STAGES.map((target) => (
                      <button
                        key={target.id}
                        disabled={lead.stage === target.id}
                        onClick={() => {
                          onUpdateStage(lead.id, target.id);
                          setMovingLeadId(null);
                        }}
                        className={`w-full py-2 px-3 rounded-lg text-xs font-bold flex items-center justify-between transition-all ${
                          lead.stage === target.id
                            ? "bg-slate-800/40 text-slate-600 cursor-not-allowed"
                            : "bg-slate-900 hover:bg-amber-500/10 text-slate-200 hover:text-amber-300 border border-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${target.dotColor}`} />
                          <span>{target.title}</span>
                        </div>
                        {lead.stage === target.id ? (
                          <Check className="w-3.5 h-3.5 text-slate-600" />
                        ) : (
                          <ArrowRight className="w-3 h-3 text-slate-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
